import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Chip, Paper, Stack, TextField, Typography,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { lmsService } from '../../services/api';

export default function LmsSyncPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [base, setBase] = useState('');
  const [hasCreds, setHasCreds] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [vectorizing, setVectorizing] = useState(false);
  const [vecResult, setVecResult] = useState(null);
  const [ragQ, setRagQ] = useState('');
  const [ragItems, setRagItems] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [sRes, stRes, aiRes] = await Promise.all([
        lmsService.seSettings(),
        lmsService.seStatus(),
        lmsService.aiStatus(),
      ]);
      setEmail(sRes.data.email || '');
      setBase(sRes.data.base || '');
      setHasCreds(Boolean(sRes.data.has_creds));
      setStatus(stRes.data || { state: 'idle' });
      setAi(aiRes.data);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (status.state !== 'running') return undefined;
    const t = setInterval(async () => {
      try {
        const res = await lmsService.seStatus();
        setStatus(res.data || {});
        if (res.data?.state !== 'running') setSyncing(false);
      } catch (_) { /* ignore */ }
    }, 2000);
    return () => clearInterval(t);
  }, [status.state]);

  const saveCreds = async () => {
    setSaving(true);
    try {
      await lmsService.seSettingsSave({ email, password: password || undefined, base });
      setPassword('');
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const startSync = async () => {
    setSyncing(true);
    try {
      await lmsService.seSync({ email: email || undefined, password: password || undefined });
      setPassword('');
      const res = await lmsService.seStatus();
      setStatus(res.data || {});
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
      setSyncing(false);
    }
  };

  const runVectorize = async () => {
    setVectorizing(true);
    setVecResult(null);
    try {
      const res = await lmsService.ragVectorize({ only_missing: true });
      setVecResult(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setVectorizing(false);
    }
  };

  const runSearch = async () => {
    if (!ragQ.trim()) return;
    try {
      const res = await lmsService.ragSearch(ragQ.trim(), 8);
      setRagItems(res.data?.items || []);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const stateColor = {
    idle: 'default',
    running: 'info',
    done: 'success',
    error: 'error',
  }[status.state] || 'default';

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>AI / RAG / SmartExpert</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <AutoAwesomeIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>AI</Typography>
          {ai && (
            <Chip
              size="small"
              label={ai.available ? 'доступний' : 'немає ключа'}
              color={ai.available ? 'success' : 'default'}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ключі: OPENAI_API_KEY або ANTHROPIC_API_KEY у середовищі бекенду.
          Embeddings (RAG) — лише OpenAI.
        </Typography>
        {ai && (
          <Typography variant="caption" color="text.secondary">
            OpenAI: {ai.openai ? 'так' : 'ні'} · Anthropic: {ai.anthropic ? 'так' : 'ні'} · Embed: {ai.embed_available ? 'так' : 'ні'}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CloudSyncIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>SmartExpert</Typography>
          <Chip size="small" label={hasCreds ? 'облікові дані збережено' : 'немає логіну'} color={hasCreds ? 'success' : 'default'} />
          <Chip size="small" label={status.state || 'idle'} color={stateColor} />
        </Stack>
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <TextField
            label="Email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label="Пароль"
            type="password"
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={hasCreds ? '•••••••• (залиште порожнім, щоб не змінювати)' : ''}
            fullWidth
          />
          <TextField
            label="Base URL"
            size="small"
            value={base}
            onChange={(e) => setBase(e.target.value)}
            placeholder="https://….smartexpert.tech"
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={saveCreds} disabled={saving}>
              {saving ? 'Збереження…' : 'Зберегти'}
            </Button>
            <Button
              variant="contained"
              onClick={startSync}
              disabled={syncing || status.state === 'running'}
              startIcon={status.state === 'running' ? <CircularProgress size={16} color="inherit" /> : <CloudSyncIcon />}
            >
              Синхронізувати
            </Button>
          </Stack>
          {status.msg && (
            <Alert severity={status.state === 'error' ? 'error' : status.state === 'done' ? 'success' : 'info'}>
              {status.msg}
              {(status.nav || status.kb) ? ` (навчання: ${status.nav || 0}, база: ${status.kb || 0})` : ''}
            </Alert>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>RAG</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={runVectorize} disabled={vectorizing}>
            {vectorizing ? 'Векторизація…' : 'Векторизувати матеріали'}
          </Button>
          {vecResult && (
            <Chip
              size="small"
              label={`готово ${vecResult.done}, пропущено ${vecResult.skipped}, помилок ${vecResult.errors}`}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1, maxWidth: 560 }}>
          <TextField
            size="small"
            fullWidth
            label="Пошук по матеріалах"
            value={ragQ}
            onChange={(e) => setRagQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          />
          <Button variant="contained" onClick={runSearch}>Знайти</Button>
        </Stack>
        {ragItems.map((it) => (
          <Box key={`${it.kind}-${it.ref_id}`} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={600}>
              {it.title || it.ref_id}
              {typeof it.score === 'number' ? ` · ${it.score}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {it.source} — {(it.body || '').slice(0, 200)}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}
